import { useState, useCallback, useEffect, useRef } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { useSearchStore } from '@/stores/searchStore';
import { copyToClipboard } from '@/utils/clipboard';
import { buildViewLink, buildPinLink } from './shareUrl';

/**
 * Floating "Share" button. Builds a shareable link for the current view — or,
 * when a place is selected, a `?ll=` pin at that place — copies it to the
 * clipboard with a toast, and reveals a small popover showing the link with a
 * copy button as a fallback (e.g. if the clipboard API is blocked).
 *
 * Top of the bottom-right FAB rail, above My Location (@bottom-56) — 64px pitch
 * keeps it from overlapping the stack below (My Location / GeoChat / hazard).
 */
export function ShareControl() {
  const map = useMapStore((s) => s.map);
  const addNotification = useUIStore((s) => s.addNotification);
  const selectedResult = useSearchStore((s) => s.selectedResult);

  const [open, setOpen] = useState(false);
  const [link, setLink] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  const buildLink = useCallback((): string => {
    // Prefer a precise pin when a place is selected; otherwise share the view.
    if (selectedResult) {
      return buildPinLink(selectedResult.coordinates, map?.getZoom());
    }
    if (map) {
      const center = map.getCenter();
      return buildViewLink(map.getZoom(), { lat: center.lat, lng: center.lng });
    }
    return window.location.href;
  }, [selectedResult, map]);

  const handleShare = useCallback(async () => {
    const url = buildLink();
    setLink(url);
    setOpen(true);
    const ok = await copyToClipboard(url);
    addNotification({
      type: ok ? 'success' : 'info',
      message: ok ? 'Link copied' : 'Copy the link below',
      duration: 3000,
    });
  }, [buildLink, addNotification]);

  const handleCopyFromPopover = useCallback(async () => {
    const ok = await copyToClipboard(link);
    addNotification({
      type: ok ? 'success' : 'error',
      message: ok ? 'Link copied' : 'Failed to copy link',
      duration: 3000,
    });
  }, [link, addNotification]);

  // Close the popover on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={popoverRef} className="absolute bottom-72 right-4 z-controls">
      <button
        onClick={handleShare}
        title="Share this location"
        aria-label="Share this location"
        className={`flex h-10 w-10 max-md:h-11 max-md:w-11 items-center justify-center rounded-lg bg-white shadow-card transition-all hover:bg-gray-50 active:scale-95 ${
          open ? 'ring-2 ring-primary-500' : ''
        }`}
      >
        <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-0 right-full mr-2 w-72 max-w-[calc(100vw-5rem)] rounded-lg bg-white p-3 shadow-dropdown animate-fade-in">
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            {selectedResult ? 'Share this place' : 'Share this view'}
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700"
            />
            <button
              onClick={handleCopyFromPopover}
              className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-700"
            >
              Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
